import React, { useState, useRef, RefObject, MutableRefObject } from "react";
import { RaceWagerType } from "@tvg/ts-types/Race";
import { Icon, Paragraph, useColorTokens } from "@tvg/design-system";
import useOnClickOutside from "@tvg/design-system/src/hooks/useOnClickOutside";
import {
  SimpleSelectAllContainer,
  SimpleSelectAllButton,
  DropdownSelectAllContainer
} from "../../styled-components";
import DropdownElement from "./DropdownElement";

interface DropdownSelectionsProps {
  selectionLabels: Array<string>;
  betType: RaceWagerType;
  isAllSelected: boolean[];
  onClickSelectAll: (position: number, isAllSelected: boolean) => void;
}

const DropdownSelections = ({
  selectionLabels,
  betType,
  isAllSelected,
  onClickSelectAll
}: DropdownSelectionsProps) => {
  const colorTokens = useColorTokens();
  const ref: MutableRefObject<HTMLDivElement | undefined> = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const hasOneAllSelected: boolean = isAllSelected.includes(true);

  const onClick = () => {
    setIsOpen((prevState) => !prevState);
  };

  useOnClickOutside(ref as RefObject<HTMLElement>, () => {
    setIsOpen(false);
  });

  const renderDropdown = () => (
    <DropdownSelectAllContainer data-qa-label="dropdown-select-all-container">
      {selectionLabels.map((label: string, index: number) => (
        <DropdownElement
          betType={betType}
          label={label}
          key={`${label}-${betType.type.code}-selection`}
          isAllSelected={isAllSelected[index as number]}
          onClickSelectAll={() =>
            onClickSelectAll(index, isAllSelected[index as number])
          }
        />
      ))}
    </DropdownSelectAllContainer>
  );

  return (
    <SimpleSelectAllContainer ref={ref as MutableRefObject<HTMLDivElement>}>
      <SimpleSelectAllButton
        data-qa-label="simple-select-all-button"
        isAllSelected={hasOneAllSelected}
        onClick={onClick}
      >
        <Paragraph
          qaLabel="select-all-dropdown-text"
          color={
            hasOneAllSelected
              ? colorTokens.component.button.secondary.content.hover
              : colorTokens.component.button.tertiary.content.base
          }
          fontSize="xs"
          fontFamily="medium"
        >
          Select all
        </Paragraph>
        <Icon
          name={isOpen ? "chevronUp" : "chevronDown"}
          size="s"
          ml={5}
          lineColor={
            hasOneAllSelected
              ? colorTokens.component.button.secondary.content.hover
              : colorTokens.component.button.tertiary.content.base
          }
        />
      </SimpleSelectAllButton>
      {isOpen && renderDropdown()}
    </SimpleSelectAllContainer>
  );
};

export default DropdownSelections;
